import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { Layout, Menu, Divider } from "antd";
import { Context } from "contexts/Provider";
import { logout } from "contexts/auth/Actions";
import logo from "../../assets/logo.png";

import {
  TeamOutlined,
  UnorderedListOutlined,
  LogoutOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
  FileSearchOutlined,
  ExportOutlined,
  CloudDownloadOutlined,
  DollarOutlined,
  MobileOutlined,
  FileAddOutlined,
  FileProtectOutlined,
  DashboardOutlined,
  WarningOutlined,
  CarOutlined,
  LinkOutlined,
  ToolOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  ReadOutlined,
} from "@ant-design/icons";

import {
  Container,
  FullHeightLayout,
  CustomSider,
  LogoPlaceHolder,
  CustomHeader,
  FullHeightRow,
  HamburgerButton,
  ContentWithPadding,
} from "./themeStyles";

import { useHistory, useLocation } from "react-router-dom";

export interface IThemeProps {
  children: React.ReactNode;
}

const PATH_TO_SUBMENU: Record<string, string> = {
  "/": "users",
  "/devices": "users",
  "/referral": "users",
  "/dashboard": "analytics",
  "/driving-score": "analytics",
  "/amodo-data": "analytics",
  "/quotes": "insurance",
  "/ncd": "insurance",
  "/breakdown": "insurance",
  "/mot-failure": "vehicles",
  "/vehicle-extended-data": "vehicles",
  "/questions": "content",
  "/answers": "content",
  "/links": "content",
};

const Theme: FC<IThemeProps> = ({ children }: IThemeProps) => {
  const history = useHistory();
  const location = useLocation();

  const {
    state: { auth },
    dispatch,
  } = useContext(Context);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(true);

  const activeSubmenu = PATH_TO_SUBMENU[location.pathname];
  const [openKeys, setOpenKeys] = useState<string[]>(
    activeSubmenu ? [activeSubmenu] : []
  );

  useEffect(() => {
    if (activeSubmenu) {
      setOpenKeys((prev) =>
        prev.includes(activeSubmenu) ? prev : [...prev, activeSubmenu]
      );
    }
  }, [activeSubmenu]);

  const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

  return (
    <Container>
      <FullHeightLayout>
        <CustomSider
          collapsible
          collapsed={isSidebarCollapsed}
          onCollapse={setIsSidebarCollapsed}
          trigger={null}
        >
          <LogoPlaceHolder collapsed={isSidebarCollapsed}>
            <img src={logo} alt="Rooster" />
          </LogoPlaceHolder>
          <Menu
            selectedKeys={selectedKeys}
            openKeys={isSidebarCollapsed ? [] : openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            mode="inline"
          >
            <Menu.SubMenu key="users" icon={<TeamOutlined />} title="Users">
              <Menu.Item
                key="/"
                icon={<TeamOutlined />}
                onClick={() => history.push("/")}
              >
                Users
              </Menu.Item>
              <Menu.Item
                key="/devices"
                icon={<MobileOutlined />}
                onClick={() => history.push("/devices")}
              >
                Devices
              </Menu.Item>
              <Menu.Item
                key="/referral"
                icon={<DollarOutlined />}
                onClick={() => history.push("/referral")}
              >
                Referral
              </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu
              key="analytics"
              icon={<BarChartOutlined />}
              title="Analytics"
            >
              <Menu.Item
                key="/dashboard"
                icon={<DashboardOutlined />}
                onClick={() => history.push("/dashboard")}
              >
                Dashboard
              </Menu.Item>
              <Menu.Item
                key="/driving-score"
                icon={<FileProtectOutlined />}
                onClick={() => history.push("/driving-score")}
              >
                Driving Score
              </Menu.Item>
              <Menu.Item
                key="/amodo-data"
                icon={<CloudDownloadOutlined />}
                onClick={() => history.push("/amodo-data")}
              >
                Amodo Data
              </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu
              key="insurance"
              icon={<SafetyCertificateOutlined />}
              title="Insurance"
            >
              <Menu.Item
                key="/quotes"
                icon={<FileAddOutlined />}
                onClick={() => history.push("/quotes")}
              >
                Quotes
              </Menu.Item>
              <Menu.Item
                key="/ncd"
                icon={<ExportOutlined />}
                onClick={() => history.push("/ncd")}
              >
                NCD
              </Menu.Item>
              <Menu.Item
                key="/breakdown"
                icon={<ToolOutlined />}
                onClick={() => history.push("/breakdown")}
              >
                Breakdowns
              </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu
              key="vehicles"
              icon={<CarOutlined />}
              title="Vehicles"
            >
              <Menu.Item
                key="/mot-failure"
                icon={<WarningOutlined />}
                onClick={() => history.push("/mot-failure")}
              >
                MOT Failure
              </Menu.Item>
              <Menu.Item
                key="/vehicle-extended-data"
                icon={<CarOutlined />}
                onClick={() => history.push("/vehicle-extended-data")}
              >
                Extended Data
              </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu key="content" icon={<ReadOutlined />} title="Content">
              <Menu.Item
                key="/questions"
                icon={<UnorderedListOutlined />}
                onClick={() => history.push("/questions")}
              >
                Questions
              </Menu.Item>
              <Menu.Item
                key="/answers"
                icon={<FileSearchOutlined />}
                onClick={() => history.push("/answers")}
              >
                Answers
              </Menu.Item>
              <Menu.Item
                key="/links"
                icon={<LinkOutlined />}
                onClick={() => history.push("/links")}
              >
                Links
              </Menu.Item>
            </Menu.SubMenu>

            <Divider />
            {auth.isLoggedIn && (
              <Menu.Item
                key="/logout"
                icon={<LogoutOutlined />}
                onClick={() => dispatch(logout())}
              >
                Logout
              </Menu.Item>
            )}
          </Menu>
        </CustomSider>
        <Layout>
          <CustomHeader collapsed={isSidebarCollapsed}>
            <FullHeightRow align="middle">
              <HamburgerButton
                type="text"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                size={"large"}
              >
                {isSidebarCollapsed ? (
                  <CaretRightOutlined />
                ) : (
                  <CaretLeftOutlined />
                )}
              </HamburgerButton>
            </FullHeightRow>
          </CustomHeader>
          <ContentWithPadding collapsed={isSidebarCollapsed}>
            {children}
          </ContentWithPadding>
        </Layout>
      </FullHeightLayout>
    </Container>
  );
};

export default Theme;
